import { Link as RouterLink, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import type { ComponentType, MouseEventHandler, ReactNode } from 'react';

/**
 * The one place the app touches TanStack Router's link/navigate API.
 *
 * The router is defined in code (src/router.tsx) rather than registered
 * globally, so `to` is an ordinary path string here. This wrapper exists so
 * that if the router's typing ever changes, exactly one file needs touching.
 */

export interface AppLinkProps {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  replace?: boolean;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  'aria-label'?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  children?: ReactNode;
}

export const AppLink = RouterLink as unknown as ComponentType<AppLinkProps>;

export interface GoOptions {
  params?: Record<string, string>;
  replace?: boolean;
  search?: Record<string, string>;
}

/** Imperative navigation: `go('/users/$id', { params: { id } })`. */
export function useGo(): (to: string, options?: GoOptions) => void {
  const navigate = useNavigate();
  return (to, options) => {
    const run = navigate as unknown as (input: { to: string } & GoOptions) => void;
    run({ to, ...options });
  };
}

export function usePathname(): string {
  const state = useRouterState() as unknown as { location: { pathname: string } };
  return state.location.pathname;
}

/**
 * Path params for whatever route is matched, without binding to a route id.
 * Each is optional because the caller could be on any route.
 */
export function useRouteParams(): Record<string, string | undefined> {
  const run = useParams as unknown as (options: { strict: false }) => Record<string, string | undefined>;
  return run({ strict: false });
}
